import React, { useContext, useEffect, useState } from 'react'
import './OTPChannelSelectForm.scss'
import { Form, FormRadioGroup, Button, Link } from '@connectcore/connect-components-react'
import { groupChannelsByPhoneNumber } from '~utils/helpers'
import { IUserContextType, UserContext } from '~utils/context/UserContext'
import { ALIGN_BUTTONS_AND_LINKS } from '~utils/featureFlags'
import {
  OTP_CHANNEL_REQUIRED_MESSAGE,
  LOGIN_FAILURE_SCD_ERROR,
  FORGEROCK_LOGIN_STAGES,
  FORGEROCK_FORGOT_PASSWORD_STAGES,
} from '~utils/constants'
import { CallbackType } from '@forgerock/javascript-sdk'
import TermsAndConditions from '~components/shared/OTPChannelSelectForm/TermsAndConditions'

interface IOTPChannelProps {
  value: number | null
  text: string | null
  phoneNumber: string | null
  email: string | null
}

const OTPChannelSelectForm = () => {
  const [data, setData] = useState({
    otpChannel: { value: null, text: null, phoneNumber: null, email: null },
  })
  const [error, setError] = useState('')
  const [showTerms, setShowTerms] = useState(false)
  const [otpChannel, setOTPChannel] = useState({
    value: null,
    text: null,
    phoneNumber: null,
    email: null,
  })
  const [OTPGroups, setOTPGroups] = useState<IOTPChannelProps[][]>([
    [{ value: null, text: null, phoneNumber: null, email: null }],
  ])
  const [apiError, setApiError] = useState(false)
  const { step, nextStep, showErr, setSelectedOTPChannel } = useContext(
    UserContext
  ) as IUserContextType

  let choiceCallBackFn

  if (step?.getCallbacksOfType(CallbackType.ChoiceCallback)?.length > 0)
    choiceCallBackFn = step?.getCallbackOfType(CallbackType.ChoiceCallback)

  const availableChannels = JSON.parse(choiceCallBackFn.getOutputByName('choices')[0]).map(
    channel => {
      channel.text = channel.type
      return channel
    }
  )

  const stage: string = step?.payload?.stage

  useEffect(() => setOTPGroups(groupChannelsByPhoneNumber(availableChannels)), [])

  useEffect(() => {
    setApiError(showErr)
  }, [showErr])

  const submitSelectedOTPChannel = () => {
    const selectedOTPValue = data?.otpChannel?.value
    const selectedOTPType = data?.otpChannel?.text
    if (selectedOTPValue !== null) {
      setSelectedOTPChannel(selectedOTPType)
      choiceCallBackFn?.setInputValue(selectedOTPValue)
      nextStep(step)
    } else {
      setError(OTP_CHANNEL_REQUIRED_MESSAGE)
    }
  }

  return (
    <>
      {showTerms && <TermsAndConditions setShowTerms={setShowTerms} />}
      {!showTerms && (
        <Form
          className="OTPChannelSelectForm d-flex flex-column wide-form padding-bottom-scrlbr"
          data-testid="OTPChannelSelectForm"
          onChange={e => {
            setData({ ...data, ...e })
            setApiError(false)
          }}
          onSubmit={() => submitSelectedOTPChannel()}
          key="OTPChannelSelectForm"
        >
          <div>
            {stage === FORGEROCK_LOGIN_STAGES.LOGIN_OTP_DELIVERY_CHANNEL && (
              <h2 className="fw-bold my-5 align-self-center">
                Step 2 - Select how you want to receive a one-time access code
              </h2>
            )}

            {stage === FORGEROCK_FORGOT_PASSWORD_STAGES.FORGOT_PASSWORD_OTP_DELIVERY_CHANNEL && (
              <h2 className="fw-bold my-5 align-self-center">
                Password change - Step 2 Select how you want to receive a one-time access code
              </h2>
            )}

            <div>
              <p>
                We need to confirm your identity to ensure your accounts are secure. We do this by
                sending a one-time access code to the email address or phone number you provided us
                in the past.
                <br />
                Choose how you&apos;d like to receive this access code and click Next. On the next
                page, you&apos;ll enter the access code you receive.
              </p>
            </div>

            {OTPGroups?.some(item => item?.[0]?.email) && <b>Email:</b>}

            {OTPGroups?.map(
              (OTPItem, index) =>
                OTPItem[0].email && (
                  <FormRadioGroup
                    label={<span className="mx-5 w-75">{OTPItem[0].email}</span>}
                    inline
                    name="otpChannel"
                    inputClassName="my-5"
                    data={OTPItem}
                    value={otpChannel}
                    onChange={e => {
                      setError('')
                      const { value } = e?.value
                      choiceCallBackFn.setInputValue(value)
                      setOTPChannel(e?.value)
                    }}
                    key={index}
                    className="d-flex align-items-center"
                  />
                )
            )}

            {OTPGroups?.some(item => item?.[0]?.phoneNumber) && <b>Phone:</b>}

            {OTPGroups?.map(
              (OTPItem, index) =>
                OTPItem[0].phoneNumber && (
                  <FormRadioGroup
                    label={<span className="mx-5 w-75">{OTPItem[0].phoneNumber}</span>}
                    inline
                    name="otpChannel"
                    inputClassName="my-5"
                    data={OTPItem}
                    value={otpChannel}
                    onChange={e => {
                      setError('')
                      const { value } = e?.value
                      choiceCallBackFn.setInputValue(value)
                      setOTPChannel(e?.value)
                    }}
                    key={index}
                    className="d-flex align-items-center"
                  />
                )
            )}

            {error && (
              <p className="invalid-feedback" role="alert">
                {error}
              </p>
            )}
            {apiError && (
              <p className="invalid-feedback" role="alert">
                {LOGIN_FAILURE_SCD_ERROR}
              </p>
            )}

            <div className="alert-disclaimer">
              JPMorgan Chase Alerts and Notifications: Text HELP to 72152 for assistance, or email{' '}
              <Link href="mailto:jpmmhelp@jpmorgan.com">jpmmhelp@jpmorgan.com.</Link> Message and
              data rates may apply. 1 message per request. Text STOP to 72152 to cancel.
              Participating carriers are ACG, ALLTEL/AWC, AT&T Mobility, Boost, Cincinnati Bell,
              Google Voice, Metro PCS, Rural Carrier Groups, Sprint, Tier 2/3 Group, T-Mobile, U.S.
              Cellular, Verizon Wireless, and Virgin Mobile.{' '}
            </div>

            <div className="d-flex flex-column">
              <Button kind="primary" className={`my-3 align-${ALIGN_BUTTONS_AND_LINKS}`}>
                Next
              </Button>
            </div>

            <div className="privacy-disclaimer my-5">
              <Link href="https://www.jpmorgan.com/pages/privacy" target="_blank">
                Privacy Policy
              </Link>
              <Link
                href="#"
                onClick={e => {
                  e.preventDefault()
                  setShowTerms(true)
                }}
                className="mx-3"
              >
                Mobile Terms & Conditions
              </Link>
              <p className="my-3">
                Note: For security reasons, we&apos;ve hidden parts of your contact information with
                &quot;***&quot;.
              </p>
            </div>
          </div>
        </Form>
      )}
    </>
  )
}

export default OTPChannelSelectForm
